import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { getUpcomingPayables, getUpcomingReceivables } from '../../../services/financialService';

const AlertsSidebar = ({ overduePayables, overdueReceivables }) => {
  const [upcomingPayables, setUpcomingPayables] = useState([]);
  const [upcomingReceivables, setUpcomingReceivables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUpcomingItems();
  }, []);

  const fetchUpcomingItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const [payables, receivables] = await Promise.all([
        getUpcomingPayables(),
        getUpcomingReceivables()
      ]);
      setUpcomingPayables(payables);
      setUpcomingReceivables(receivables);
    } catch (err) {
      console.error('Error loading upcoming items:', err);
      setError(err?.message || 'Erro ao carregar alertas');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('pt-BR');
  };

  const getDaysUntilDue = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Alertas e Pendências</h3>
        <div className="space-y-3">
          {[1, 2, 3]?.map((i) => (
            <div key={i} className="p-3 bg-muted/30 rounded animate-pulse">
              <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Alertas e Pendências</h3>
        <div className="text-center py-8">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
          <p className="text-destructive font-medium mb-2">Erro ao carregar alertas</p>
          <p className="text-sm text-muted-foreground mb-4">{error}</p>
          <button
            onClick={fetchUpcomingItems}
            className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  const hasOverdue = overduePayables > 0 || overdueReceivables > 0;
  const hasUpcoming = upcomingPayables?.length > 0 || upcomingReceivables?.length > 0;

  // Add this block - Create alerts array from the data
  const alerts = [];

  // Add overdue payables alert
  if (overduePayables > 0) {
    alerts.push({
      id: 'overdue-payables',
      type: 'danger',
      title: 'Contas a Pagar Vencidas',
      message: `Você tem ${overduePayables} conta(s) a pagar vencida(s)`
    });
  }

  // Add overdue receivables alert
  if (overdueReceivables > 0) {
    alerts.push({
      id: 'overdue-receivables',
      type: 'danger',
      title: 'Contas a Receber Vencidas',
      message: `Você tem ${overdueReceivables} conta(s) a receber vencida(s)`
    });
  }

  // Add upcoming payables alerts
  upcomingPayables?.forEach((payable, index) => {
    const daysUntil = getDaysUntilDue(payable.dueDate);
    alerts.push({
      id: `upcoming-payable-${index}`,
      type: daysUntil <= 3 ? 'warning' : 'info',
      title: `Conta a Pagar - ${payable.description || 'Sem descrição'}`,
      message: `Vence em ${daysUntil} dia(s) - ${formatCurrency(payable.amount)} em ${formatDate(payable.dueDate)}`
    });
  });

  // Add upcoming receivables alerts
  upcomingReceivables?.forEach((receivable, index) => {
    const daysUntil = getDaysUntilDue(receivable.dueDate);
    alerts.push({
      id: `upcoming-receivable-${index}`,
      type: daysUntil <= 3 ? 'warning' : 'info',
      title: `Conta a Receber - ${receivable.description || 'Sem descrição'}`,
      message: `Vence em ${daysUntil} dia(s) - ${formatCurrency(receivable.amount)} em ${formatDate(receivable.dueDate)}`
    });
  });

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
      <h2 className="text-xl font-bold text-gray-900 mb-6">Alertas Financeiros</h2>
      
      <div className="space-y-4">
        {alerts?.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Nenhum alerta no momento</p>
          </div>
        ) : (
          alerts?.map((alert) => (
            <div
              key={alert?.id}
              className={`p-4 rounded-lg border ${
                alert?.type === 'warning' ?'bg-yellow-50 border-yellow-200'
                  : alert?.type === 'danger' ?'bg-red-50 border-red-200' :'bg-blue-50 border-blue-200'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <h3 className={`font-medium mb-1 ${
                    alert?.type === 'warning' ?'text-yellow-800'
                      : alert?.type === 'danger' ?'text-red-800' :'text-blue-800'
                  }`}>
                    {alert?.title}
                  </h3>
                  <p className={`text-sm ${
                    alert?.type === 'warning' ?'text-yellow-700'
                      : alert?.type === 'danger' ?'text-red-700' :'text-blue-700'
                  }`}>
                    {alert?.message}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AlertsSidebar;